/**
 * ExternalResultDialog — add or edit ONE result from a rodeo HighCall did not
 * run: amount, event type, rodeo name, date, placement, optional notes.
 *
 * The event type list comes from the server (`allowed_event_type_keys`), so
 * team roping is only ever offered as "Team Roping — Header" / "— Heeler" —
 * the board filters by position and a plain "Team Roping" result would have
 * nowhere to land. The date must fall in the season being edited; the server
 * derives `season_year` from it and refuses anything else, so the same check
 * here just saves a round trip.
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type {
  ExternalResultInput,
  SeasonExternalResult,
} from '@/api/extensions/useSeasonWinningsOps';

export interface ExternalResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The season the result must fall in. */
  seasonYear: number;
  /** Server-owned list; drives the event type picker. */
  allowedEventTypeKeys: string[];
  /** When set, the dialog edits this result instead of adding one. */
  editing?: SeasonExternalResult | null;
  onSubmit: (values: ExternalResultInput) => void;
  isPending: boolean;
}

interface Draft {
  amount: string;
  eventTypeKey: string;
  rodeoName: string;
  eventDate: string;
  placement: string;
  notes: string;
}

function draftFrom(editing: SeasonExternalResult | null | undefined): Draft {
  if (!editing) {
    return {
      amount: '',
      eventTypeKey: '',
      rodeoName: '',
      eventDate: '',
      placement: '',
      notes: '',
    };
  }
  return {
    amount: editing.amount.toFixed(2),
    eventTypeKey: editing.event_type_key,
    rodeoName: editing.rodeo_name,
    eventDate: editing.event_date,
    placement: editing.placement != null ? String(editing.placement) : '',
    notes: editing.notes ?? '',
  };
}

/** Validate a draft into the request payload, or say what is wrong. */
export function parseExternalResultDraft(
  draft: Draft,
  seasonYear: number,
): { ok: true; values: ExternalResultInput } | { ok: false; error: string } {
  const amount = draft.amount.trim() === '' ? NaN : Number(draft.amount);
  if (!Number.isFinite(amount) || amount < 0) {
    return { ok: false, error: 'Enter the amount won — zero or more.' };
  }
  if (!draft.eventTypeKey) return { ok: false, error: 'Choose the event.' };
  const rodeoName = draft.rodeoName.trim();
  if (!rodeoName) return { ok: false, error: 'Enter the rodeo name.' };
  const date = draft.eventDate.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return { ok: false, error: 'Enter the rodeo date.' };
  if (Number(date.slice(0, 4)) !== seasonYear) {
    return { ok: false, error: `The date must fall in the ${seasonYear} season.` };
  }
  let placement: number | null = null;
  if (draft.placement.trim() !== '') {
    placement = Number(draft.placement);
    if (!Number.isInteger(placement) || placement < 1) {
      return { ok: false, error: 'Placement must be a whole number, 1 or more — or leave it blank.' };
    }
  }
  return {
    ok: true,
    values: {
      amount: Math.round(amount * 100) / 100,
      event_type_key: draft.eventTypeKey,
      rodeo_name: rodeoName,
      event_date: date,
      placement,
      notes: draft.notes.trim() || null,
    },
  };
}

export function ExternalResultDialog({
  open,
  onOpenChange,
  seasonYear,
  allowedEventTypeKeys,
  editing,
  onSubmit,
  isPending,
}: ExternalResultDialogProps) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(editing));
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever the dialog opens for a different result (or to add).
  useEffect(() => {
    if (open) {
      setDraft(draftFrom(editing));
      setError(null);
    }
  }, [open, editing]);

  const set = <K extends keyof Draft>(key: K) => (value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    const parsed = parseExternalResultDraft(draft, seasonYear);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError(null);
    onSubmit(parsed.values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit event winnings' : 'Add event winnings'}</DialogTitle>
          <DialogDescription>
            One event at a rodeo HighCall did not run. It counts toward this
            contestant's {seasonYear} total and shows under its event on the
            standings.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="external-amount">Amount won *</Label>
            <CurrencyInput
              id="external-amount"
              value={draft.amount}
              onChange={set('amount')}
              aria-label="Amount won"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="external-event-type">Event *</Label>
            <Select value={draft.eventTypeKey} onValueChange={set('eventTypeKey')}>
              <SelectTrigger id="external-event-type" aria-label="Event">
                <SelectValue placeholder="Choose an event" />
              </SelectTrigger>
              <SelectContent>
                {allowedEventTypeKeys.map((key) => (
                  <SelectItem key={key} value={key}>
                    {key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="external-rodeo-name">Rodeo *</Label>
            <Input
              id="external-rodeo-name"
              value={draft.rodeoName}
              onChange={(e) => set('rodeoName')(e.target.value)}
              placeholder="e.g. Vale Rodeo"
              maxLength={200}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="external-date">Date *</Label>
            <Input
              id="external-date"
              type="date"
              value={draft.eventDate}
              onChange={(e) => set('eventDate')(e.target.value)}
              min={`${seasonYear}-01-01`}
              max={`${seasonYear}-12-31`}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="external-placement">Placement</Label>
            <Input
              id="external-placement"
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={draft.placement}
              onChange={(e) => set('placement')(e.target.value)}
              placeholder="e.g. 1"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="external-notes">Notes (optional)</Label>
            <Textarea
              id="external-notes"
              value={draft.notes}
              onChange={(e) => set('notes')(e.target.value)}
              rows={2}
              maxLength={2000}
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editing ? 'Save changes' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
